import { Workflow } from '../../../../enums/orders';
import { DriverType } from '../../../../interfaces/delivery-company/drivers';
import {
  NotificationStage,
  NotificationTemplate,
  OrderType,
} from '../../../../interfaces/delivery-company/orders';
import {
  IOrderDeliveryTask,
  IPaymentTask,
  IPickupTask,
} from '../../../../interfaces/delivery-company/tasks';
import { get_cod } from '../../../../utils/cod';
import { displayLocation } from '../../../../utils/locations';
import { displayMoney } from '../../../../utils/money';
import order from '../../order/order';

export const generateMessageContent = (body: string) => {
  return `
    Hello Jack 👋🏻
    I love
    `;
};

export const formatMessageWithLineBreaks = (message: string, orgId: string) => {
  message = message.replaceAll('{{recipient_name}}', 'Jack');
  message = message.replaceAll('{{merchant_name}}', 'Happy Shop');
  message = message.replaceAll(
    '{{tracking_link}}',
    'https://parceltracer.app/tracking/?1241274'
  );
  message = message.replaceAll('{{driver_name}}', 'Alex');
  message = message.replaceAll('{{order_id}}', '1202');
  message = message.replaceAll('{{company_name}}', orgId);
  message = message.replaceAll('{{total_amount}}', '5$ + 200000L.L');
  message = message.replaceAll('{{recipient_address}}', 'Hamra Street');
  message = message.replaceAll('{{recipient_region}}', 'Aley / Bchamoun');

  return message.split('\n').map((line, index) => (
    <span key={index}>
      {line}
      <br />
    </span>
  ));
};

export interface IResolveTemplateArgs {
  recipient_name: string;
  recipient_address: string;
  recipient_region: string;
  merchant_name: string;
  tracking_link: string;
  driver_name: string;
  company_name: string;
  total_amount: string;
  order_id?: string;
}

export const buildResolveTemplateArgsForDelivery = (
  order: OrderType,
  driver_name: string,
  orgId: string
): IResolveTemplateArgs => {
  const isStandard = order.workflow === Workflow.STANDARD;
  const baseResult = {
    merchant_name: order.merchant?.name ?? '',
    tracking_link: `prod.parceltracer.app/tracking/${order.tracking_hash}/`,
    driver_name: driver_name,
    company_name: orgId,
    total_amount: displayMoney(get_cod(order)),
  };
  if (isStandard)
    return {
      ...baseResult,
      recipient_name: order.customer?.name ?? '',
      recipient_address: displayLocation(order.customer_location),
      recipient_region: `${order.customer_location?.area?.name_en}/${order.customer_location?.area?.name_ar}`,
    };
  else {
    return {
      ...baseResult,
      recipient_name: order.merchant_location?.contact_person ?? '',
      recipient_address: displayLocation(order.merchant_location),
      recipient_region: `${order.merchant_location?.area?.name_en}/${order.merchant_location?.area?.name_ar}`,
    };
  }
};

export const buildResolveTemplateArgsForPickup = (
  pickupTask: IPickupTask,
  driver_name: string,
  orgId: string
): IResolveTemplateArgs => {
  const isStandard = pickupTask.workflow === Workflow.STANDARD;
  const baseResult = {
    merchant_name: pickupTask.merchant?.name ?? '',
    tracking_link: '',
    driver_name: driver_name,
    company_name: orgId,
    total_amount: '',
  };
  if (!isStandard)
    return {
      ...baseResult,
      recipient_name: pickupTask.customer?.name ?? '',
      recipient_address: displayLocation(pickupTask.customer_location),
      recipient_region: `${pickupTask.customer_location!.area!.name_en}/${pickupTask.customer_location!.area!.name_ar}`,
    };
  else {
    return {
      ...baseResult,
      recipient_name: pickupTask.merchant_location?.contact_person ?? '',
      recipient_address: displayLocation(pickupTask.merchant_location),
      recipient_region: `${pickupTask.merchant_location!.area!.name_en} - ${pickupTask.merchant_location!.area!.name_ar}`,
    };
  }
};

export const buildResolveTemplateArgsForPayment = (
  paymentTask: IPaymentTask,
  driver_name: string,
  orgId: string
): IResolveTemplateArgs => {
  return {
    merchant_name: paymentTask.merchant?.name ?? '',
    tracking_link: '',
    driver_name: driver_name,
    company_name: orgId,
    total_amount: paymentTask.total_due
      ? displayMoney(paymentTask.total_due)
      : 'ERROR',
    recipient_name: paymentTask.merchant_location?.contact_person ?? '',
    recipient_address: displayLocation(paymentTask.merchant_location),
    recipient_region: `${paymentTask.merchant_location!.area!.name_en} - ${paymentTask.merchant_location!.area!.name_ar}`,
  };
};

export const resolveTemplate = (
  message: string,
  args: IResolveTemplateArgs
) => {
  message = message.replaceAll('{{recipient_name}}', args.recipient_name);
  message = message.replaceAll('{{recipient_address}}', args.recipient_address);
  message = message.replaceAll('{{recipient_region}}', args.recipient_region);
  message = message.replaceAll('{{merchant_name}}', args.merchant_name);
  message = message.replaceAll('{{tracking_link}}', args.tracking_link);
  message = message.replaceAll('{{driver_name}}', args.driver_name);
  message = message.replaceAll('{{order_id}}', args.order_id??'');
  message = message.replaceAll('{{company_name}}', args.company_name);
  message = message.replaceAll('{{total_amount}}', args.total_amount);

  return message;
};

export const sentMessageColor = '#ece5dd'; // WhatsApp green for sent messages
export const screenBackgroundColor = '#f4f4f4'; // Soft gray for the screen background

export const ORG_STAGES: NotificationStage[] = ['Pickup', 'Delivery', 'Payment'];
export const MERCHANT_STAGES: NotificationStage[] = ['Arrive At Warehouse'];
export const ALL_STAGES: NotificationStage[] = [...ORG_STAGES, ...MERCHANT_STAGES];
export interface TemplateVariable {
  code: string;
  label: string;
  stages: NotificationStage[];
}
export const templateVariables: TemplateVariable[] = [
  { code: '{{tracking_link}}', label: 'Tracking Link', stages: ['Delivery', 'Arrive At Warehouse'] },
  { code: '{{recipient_name}}', label: 'Recipient Name', stages: ALL_STAGES },
  { code: '{{recipient_address}}', label: 'Recipient Address', stages: ALL_STAGES },
  { code: '{{recipient_region}}', label: 'Recipient Region', stages: ALL_STAGES },
  { code: '{{merchant_name}}', label: 'Merchant Name', stages: ALL_STAGES },
  { code: '{{driver_name}}', label: 'Driver Name', stages: ORG_STAGES },
  { code: '{{order_id}}', label: 'Order ID', stages: [...MERCHANT_STAGES, ...ORG_STAGES] },
  {
    code: '{{total_amount}}',
    label: 'Total Amount',
    stages: ['Delivery', 'Payment'],
  },
  { code: '{{company_name}}', label: 'Company Name', stages: ALL_STAGES },
];

export const getInvalidTemplateVars = (
  message: string,
  stages: NotificationStage[]
): TemplateVariable[] => {
  const invalidTemplatesVars: TemplateVariable[] = [];
  templateVariables.forEach((t) => {
    let stageIsOk = true;
    stages.forEach((selectedStage) => {
      stageIsOk = stageIsOk && t.stages.includes(selectedStage);
    });

    if (message.includes(t.code) && !stageIsOk) {
      invalidTemplatesVars.push(t);
    }
  });

  return invalidTemplatesVars;
};
